import { unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type PushState = Schemas['PushStateDto'];

const key = ['me', 'push'] as const;

export function usePushState() {
  return useQuery({
    queryKey: key,
    queryFn: async () => unwrap(await api.GET('/me/push')),
  });
}

/** Whether this browser can receive push at all (iOS: only installed web apps). */
export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function bytesToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  let binary = '';
  for (const b of new Uint8Array(buffer)) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The subscription of this browser, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

export class PushPermissionDenied extends Error {}

const DEVICE_KEY = 'swarm.pushDeviceId';

/** Id of this browser's device on the server (kept to mark it in the list). */
export function localDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_KEY);
  } catch {
    return null;
  }
}

function rememberDevice(id: string | null) {
  try {
    if (id) localStorage.setItem(DEVICE_KEY, id);
    else localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Private mode: the list just cannot mark this device.
  }
}

/** Whether a subscription's `applicationServerKey` is `publicKey` (base64url). */
export function sameKey(key: ArrayBuffer | null | undefined, publicKey: string): boolean {
  return !key || bytesToBase64Url(key) === publicKey;
}

/**
 * Forgets this browser's push subscription on sign-out, so the next person
 * on a shared device gets no notifications of the previous member. Best
 * effort: sign-out goes on if this fails.
 */
export async function forgetLocalPushDevice(): Promise<void> {
  const id = localDeviceId();
  rememberDevice(null);
  try {
    if (id) await api.DELETE('/me/push/devices/{id}', { params: { path: { id } } });
  } catch {
    // Offline or already gone: the device can still be removed elsewhere.
  }
  try {
    await (await currentSubscription())?.unsubscribe();
  } catch {
    // Nothing to unsubscribe.
  }
}

/** Asks for permission (call from a click), subscribes and registers this device. */
export function useEnablePush() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (publicKey: string) => {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new PushPermissionDenied();
      const registration = await navigator.serviceWorker.ready;
      let existing = await registration.pushManager.getSubscription();
      // A subscription made with an older VAPID key would be rejected by the
      // push service (401/403) forever: replace it.
      if (existing && !sameKey(existing.options.applicationServerKey, publicKey)) {
        await existing.unsubscribe();
        existing = null;
      }
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlToBytes(publicKey),
        }));
      const device = unwrap(
        await api.POST('/me/push/devices', {
          body: {
            endpoint: subscription.endpoint,
            p256dh: bytesToBase64Url(subscription.getKey('p256dh')),
            auth: bytesToBase64Url(subscription.getKey('auth')),
          },
        }),
      );
      rememberDevice(device.id);
      return device.id;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
  });
}

/** Removes a device; if it is this browser, it also unsubscribes locally. */
export function useRemoveDevice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, local }: { id: string; local: boolean }) => {
      if (local) {
        await (await currentSubscription())?.unsubscribe();
        rememberDevice(null);
      }
      unwrap(await api.DELETE('/me/push/devices/{id}', { params: { path: { id } } }));
    },
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
  });
}
